import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Loader2, CheckCircle2, MessageSquare, AlertCircle, X,
  ClipboardList, ShieldCheck, Filter, Eye, Users, ArrowRight,
} from "lucide-react";
import { useGetOpenExamDietsQuery } from "../../api/examsApi";
import {
  useCmGetResultsQuery,
  useCmGetModeratorsQuery,
  useCmGetBatchScoresQuery,
  useCmAssignModeratorMutation,
  useCmForwardToChiefExaminerMutation,
} from "../../api/chiefModeratorApi";
import { PERMS } from "../../../../utils/perms";
import { useAccess } from "../../../../utils/useAccess";

function extractError(err: any): string {
  return err?.data?.message ?? err?.data?.error ?? err?.error ?? "An error occurred";
}

const STATUS_MAP: Record<string, string> = {
  under_moderation:  "bg-purple-50 text-purple-700 border-purple-100",
  moderated:         "bg-violet-50 text-violet-700 border-violet-100",
  moderation_rework: "bg-orange-50 text-orange-700 border-orange-100",
  examiner_rework:   "bg-rose-50 text-rose-700 border-rose-100",
  published:         "bg-emerald-50 text-emerald-700 border-emerald-100",
};

const GRADE_COLOR: Record<string, string> = {
  A: "text-emerald-600", B: "text-emerald-500", C: "text-blue-500",
  D: "text-amber-500", E: "text-orange-500", F: "text-rose-500",
};

type ActionPanel =
  | { type: "assign"; id: string; moderatorId: string }
  | { type: "forward"; id: string; comment: string }
  | { type: "scores"; id: string };

export default function ChiefModeratorResultsPage() {
  const { data: diets = [], isLoading: dietsLoading } = useGetOpenExamDietsQuery();
  const { data: moderators = [] } = useCmGetModeratorsQuery();
  const [dietFilter, setDietFilter] = useState("");

  const { data: results = [], isLoading } = useCmGetResultsQuery(
    dietFilter ? { dietId: dietFilter } : undefined
  );

  const [assignModerator, { isLoading: assigning }]         = useCmAssignModeratorMutation();
  const [forwardToChief, { isLoading: forwarding }]         = useCmForwardToChiefExaminerMutation();

  const [actionError, setActionError] = useState<Record<string, string>>({});
  const [panel, setPanel] = useState<ActionPanel | null>(null);

  const { can } = useAccess();
  const mayManage = can.perm(PERMS.exam.moderationManage);

  const handleAssign = async () => {
    if (!panel || panel.type !== "assign") return;
    const { id, moderatorId } = panel;
    if (!moderatorId) {
      setActionError(p => ({ ...p, [id]: "Select a moderator." })); return;
    }
    setActionError(p => ({ ...p, [id]: "" }));
    try { await assignModerator({ id, moderator_id: moderatorId }).unwrap(); setPanel(null); }
    catch (err) { setActionError(p => ({ ...p, [id]: extractError(err) })); }
  };

  const handleForward = async () => {
    if (!panel || panel.type !== "forward") return;
    const { id, comment } = panel;
    if (!comment.trim()) {
      setActionError(p => ({ ...p, [id]: "Please add your summary comment." })); return;
    }
    setActionError(p => ({ ...p, [id]: "" }));
    try { await forwardToChief({ id, comment }).unwrap(); setPanel(null); }
    catch (err) { setActionError(p => ({ ...p, [id]: extractError(err) })); }
  };

  const selCls = "w-full border-b-2 border-gray-100 py-2 font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent text-sm";

  const pending   = (results as any[]).filter((r: any) => r.status === "under_moderation").length;
  const moderated = (results as any[]).filter((r: any) => r.status === "moderated").length;

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6 pb-20">

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[
          { label: "Total Batches",  val: (results as any[]).length, icon: ClipboardList, color: "indigo"  },
          { label: "Under Review",   val: pending,                   icon: AlertCircle,   color: "purple"  },
          { label: "Moderated",      val: moderated,                 icon: CheckCircle2,  color: "violet"  },
        ].map((s, i) => (
          <div key={i} className="bg-white border border-gray-100 p-5 rounded-3xl shadow-sm flex items-center gap-4">
            <div className={`w-11 h-11 rounded-2xl flex items-center justify-center bg-${s.color}-50 text-${s.color}-600 shrink-0`}>
              <s.icon className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">{s.label}</div>
              <div className="text-xl font-black text-gray-900">{s.val}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <Link to="/exams/chief-moderator" className="p-3 rounded-xl border bg-white hover:bg-gray-50 transition-all shadow-sm">
              <ArrowLeft className="w-4 h-4 text-gray-500" />
            </Link>
            <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200">
              <ClipboardList className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-none uppercase">Moderate Results</h1>
              <p className="text-gray-400 text-sm mt-1.5 font-medium">Assign moderators, review feedback, and forward to Chief Examiner.</p>
            </div>
          </div>

          <div className="space-y-1 w-full sm:w-64">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
              <Filter className="w-3 h-3" /> Filter by Diet
            </label>
            {dietsLoading ? (
              <div className="flex items-center gap-2 text-xs text-gray-400 py-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
            ) : (
              <select value={dietFilter} onChange={(e) => setDietFilter(e.target.value)} className={selCls}>
                <option value="">All diets</option>
                {diets.map((d: any) => <option key={d.id} value={d.id}>{d.name ?? d.title ?? d.id}</option>)}
              </select>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-100 rounded-[2.5rem] shadow-sm overflow-hidden">
        <div className="px-8 py-5 border-b border-gray-50 flex items-center justify-between">
          <div className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
            Batches {(results as any[]).length > 0 ? `(${(results as any[]).length})` : ""}
          </div>
          {isLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-300" />}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gray-200" /></div>
        ) : (results as any[]).length === 0 ? (
          <div className="py-24 text-center">
            <ClipboardList className="w-10 h-10 mx-auto text-gray-100 mb-3" />
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">No result batches available for moderation</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {(results as any[]).map((r: any) => {
              const status      = (r.status ?? "").toLowerCase();
              // Batch state says whether the act makes sense; canManage says
              // whether this account may perform it. The route only requires
              // exam.moderation.access — enough to read the board — while both
              // of these call endpoints behind exam.moderation.manage.
              const canAssign   = mayManage && (status === "under_moderation" || status === "moderation_rework");
              const canForward  = mayManage && status === "moderated";
              const showScores  = panel?.type === "scores" && panel.id === String(r.id);

              return (
                <div key={r.id} className="group">
                  <div className="flex items-start justify-between px-8 py-5 hover:bg-gray-50/80 transition-all gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-gray-900 uppercase tracking-tight">
                        {r.course_name ?? `Course ${r.course_id}`}
                      </div>
                      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                        Diet: {r.diet_name ?? r.diet_id} &nbsp;·&nbsp;
                        Examiner: {r.examiner_name ?? "—"} &nbsp;·&nbsp;
                        {r.student_count ?? 0} students
                        {r.moderator_name && <> &nbsp;·&nbsp; Moderator: {r.moderator_name}</>}
                      </div>

                      {r.moderator_comment && (
                        <div className="mt-2 bg-violet-50 border border-violet-100 rounded-xl px-4 py-2.5 text-xs text-violet-800 font-medium">
                          <span className="font-black text-[9px] uppercase tracking-widest text-violet-500 block mb-0.5">Moderator Feedback</span>
                          {r.moderator_comment}
                        </div>
                      )}
                    </div>
                    <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border shadow-sm shrink-0 ${STATUS_MAP[status] ?? "bg-gray-50 text-gray-400 border-gray-100"}`}>
                      {status.replace(/_/g, " ")}
                    </span>
                  </div>

                  <div className="px-8 pb-5 flex flex-wrap items-center gap-3">
                    {actionError[r.id] && (
                      <p className="w-full text-[10px] font-black uppercase tracking-widest text-rose-500 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" /> {actionError[r.id]}
                      </p>
                    )}

                    {canAssign && (
                      <button onClick={() => setPanel({ type: "assign", id: String(r.id), moderatorId: "" })}
                        className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-2xl px-5 py-3 text-[9px] font-black uppercase tracking-[0.15em] hover:bg-purple-600 transition-all shadow-sm active:scale-95">
                        <Users className="w-3.5 h-3.5" /> Assign Moderator
                      </button>
                    )}

                    {canForward && (
                      <button onClick={() => setPanel({ type: "forward", id: String(r.id), comment: "" })}
                        className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-2xl px-5 py-3 text-[9px] font-black uppercase tracking-[0.15em] hover:bg-orange-600 transition-all shadow-sm active:scale-95">
                        <ArrowRight className="w-3.5 h-3.5" /> Forward to Chief Examiner
                      </button>
                    )}

                    <button onClick={() => setPanel(showScores ? null : { type: "scores", id: String(r.id) })}
                      className="inline-flex items-center gap-2 border border-gray-200 text-gray-600 rounded-2xl px-5 py-3 text-[9px] font-black uppercase tracking-[0.15em] hover:bg-gray-50 transition-all active:scale-95">
                      <Eye className="w-3.5 h-3.5" /> {showScores ? "Hide Scores" : "View Scores"}
                    </button>
                  </div>

                  {showScores && <ScoresPanel batchId={String(r.id)} />}

                  {panel?.type === "assign" && panel.id === String(r.id) && (
                    <div className="mx-8 mb-5 p-5 bg-purple-50 rounded-2xl border border-purple-100 space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] font-black uppercase tracking-widest text-purple-700">Assign Moderator</p>
                        <button onClick={() => setPanel(null)} className="p-1.5 hover:bg-purple-100 rounded-xl"><X className="w-3.5 h-3.5 text-purple-600" /></button>
                      </div>
                      <select
                        value={panel.moderatorId}
                        onChange={(e) => setPanel(p => p ? { ...p, moderatorId: e.target.value } : null)}
                        className="w-full border-b-2 border-purple-200 py-2 font-bold text-gray-800 outline-none focus:border-purple-500 bg-transparent text-sm">
                        <option value="">— Select moderator —</option>
                        {(moderators as any[]).map((m: any) => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                      <div className="flex gap-3">
                        <button onClick={handleAssign} disabled={assigning}
                          className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-2xl px-5 py-3 text-[9px] font-black uppercase tracking-[0.15em] hover:bg-purple-600 transition-all disabled:opacity-30 active:scale-95">
                          {assigning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Users className="w-3.5 h-3.5" />}
                          {assigning ? "Assigning…" : "Confirm Assignment"}
                        </button>
                        <button onClick={() => setPanel(null)} className="py-3 px-5 text-[9px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-600">Cancel</button>
                      </div>
                    </div>
                  )}

                  {panel?.type === "forward" && panel.id === String(r.id) && (
                    <div className="mx-8 mb-5 p-5 bg-orange-50 rounded-2xl border border-orange-100 space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] font-black uppercase tracking-widest text-orange-700">Summary Comment to Chief Examiner</p>
                        <button onClick={() => setPanel(null)} className="p-1.5 hover:bg-orange-100 rounded-xl"><X className="w-3.5 h-3.5 text-orange-600" /></button>
                      </div>
                      <textarea rows={3}
                        value={panel.comment}
                        onChange={(e) => setPanel(p => p ? { ...p, comment: e.target.value } : null)}
                        className="w-full border-b-2 border-orange-200 py-2 font-bold text-gray-800 outline-none focus:border-orange-500 bg-transparent text-sm resize-none"
                        placeholder="Summarise the moderator's feedback and your own observations…"
                      />
                      <div className="flex gap-3">
                        <button onClick={handleForward} disabled={forwarding}
                          className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-2xl px-5 py-3 text-[9px] font-black uppercase tracking-[0.15em] hover:bg-orange-600 transition-all disabled:opacity-30 active:scale-95">
                          {forwarding ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
                          {forwarding ? "Forwarding…" : "Forward to Chief Examiner"}
                        </button>
                        <button onClick={() => setPanel(null)} className="py-3 px-5 text-[9px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-600">Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-gray-900 text-white rounded-[2.5rem] p-10 flex items-center gap-8 shadow-2xl shadow-gray-200">
        <div className="w-16 h-16 bg-violet-500/10 rounded-3xl flex items-center justify-center shrink-0 border border-violet-500/20">
          <ShieldCheck className="w-8 h-8 text-violet-400" />
        </div>
        <div>
          <h4 className="text-[11px] font-black uppercase tracking-[0.4em] text-violet-400 mb-2">Moderation Flow</h4>
          <p className="text-sm text-gray-400 leading-relaxed font-medium max-w-3xl">
            Assign each batch to a moderator for review. Once moderated, review the feedback and forward
            your consolidated summary to the Chief Examiner, who will instruct the examiner accordingly.
          </p>
        </div>
      </div>
    </div>
  );
}

function ScoresPanel({ batchId }: { batchId: string }) {
  const { data: scores = [], isLoading } = useCmGetBatchScoresQuery(batchId);

  return (
    <div className="mx-8 mb-5 border border-gray-100 rounded-2xl overflow-hidden">
      <div className="px-5 py-3 bg-gray-50 text-[9px] font-black uppercase tracking-widest text-gray-400 flex justify-between">
        <span>Student Scores</span>
        {isLoading && <Loader2 className="w-3 h-3 animate-spin" />}
      </div>
      {isLoading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-200" /></div>
      ) : scores.length === 0 ? (
        <div className="py-6 text-center text-[10px] font-black uppercase tracking-widest text-gray-300">No scores recorded</div>
      ) : (
        <div className="divide-y divide-gray-50 max-h-60 overflow-y-auto">
          {scores.map((s: any, i: number) => (
            <div key={i} className="flex items-center justify-between px-5 py-2.5 text-sm">
              <div>
                <div className="font-bold text-gray-800">{s.student_name ?? s.student_reg}</div>
                <div className="text-[10px] text-gray-400 font-mono">{s.student_reg}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-bold text-gray-700">{s.score}</span>
                <span className={`font-black text-base w-6 text-center ${GRADE_COLOR[s.grade] ?? "text-gray-500"}`}>{s.grade}</span>
                {s.version > 1 && <span className="text-[9px] bg-amber-50 text-amber-600 border border-amber-100 px-2 py-0.5 rounded-full font-black">v{s.version}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
